import { Component, OnInit, OnDestroy, ChangeDetectorRef } from '@angular/core';
import { FormBuilder, FormGroup, FormArray, Validators } from '@angular/forms';
import { Router, ActivatedRoute } from '@angular/router';
import { HttpHeaders } from '@angular/common/http';
import { AppConstants } from 'src/app/shared/constants/app.contants';
import { LoaderService } from 'src/app/shared/services/loader.service';
import { ApiService } from 'src/app/shared/services/api.service';
import { ToasterService } from 'src/app/shared/services/toaster.service';
import { TenderHelperService } from 'src/app/shared/services/tender-helper.service';
import { environment } from 'src/environments/environment';
import { lastValueFrom, Subscription } from 'rxjs';
import { EvaluationCriteriaData } from 'src/app/shared/components/evaluation-criteria/evaluation-criteria.component';

@Component({
  selector: 'app-create-tender',
  templateUrl: './create-tender.component.html',
  styleUrls: ['./create-tender.component.scss']
})
export class CreateTenderComponent implements OnInit, OnDestroy {
  AppConstants = AppConstants;
  tenderForm!: FormGroup;
  activeTab = 'Tender';
  tabs = ['Tender', 'Documents'];
  
  get entityLabel(): string {
    return this.isFwaPracticeMode ? 'FWA-Practice' : (this.isPracticeMode ? 'Practice' : 'Tender');
  }

  get entityLabelPlural(): string {
    return this.isFwaPracticeMode ? 'FWA-Practices' : (this.isPracticeMode ? 'Practices' : 'Tenders');
  }

  get entityLabelLower(): string {
    return this.entityLabel.toLowerCase();
  }

  get displayTabs(): string[] {
    return [this.entityLabel, 'Documents'];
  }
  
  get displayActiveTab(): string {
    return this.activeTab === 'Tender' ? this.entityLabel : this.activeTab;
  }
  
  setTab(tab: string): void {
    if (tab === this.entityLabel) {
      this.activeTab = 'Tender';
    } else if (tab === 'Documents') {
      this.activeTab = 'Documents';
    }
  }
  
  isTabActive(tab: string): boolean {
    if (tab === this.entityLabel) {
      return this.activeTab === 'Tender';
    } else if (tab === 'Documents') {
      return this.activeTab === 'Documents';
    }
    return false;
  }  private tenderTypeSubscription?: Subscription;
  private evaluationTypeSubscription?: Subscription;
  private technicalWeightageSubscription?: Subscription;
  private commercialWeightageSubscription?: Subscription;
  private isJoiningFeeRequiredSubscription?: Subscription;
  private tenderBondRequiredSubscription?: Subscription;
  private performanceBondRequiredSubscription?: Subscription;
  
  // Evaluation Criteria - using shared component
  evaluationCriteriaData?: EvaluationCriteriaData;
  
  // Purchase Requisition
  selectedPR: any = null;
  isPRSelected: boolean = false;
  
  // Practice vs Tender mode
  isPracticeMode: boolean = false;
  isFwaPracticeMode: boolean = false;

  // Dropdown options
  tenderTypes: string[] = ['Public', 'Private'];
  rfpRfqOptions: string[] = ['RFP', 'RFQ'];
  tenderClassifications: string[] = ['Domestic Sourcing', 'Global Sourcing'];
  currencies: string[] = [];
  currenciesFromAPI: any[] = [];
  euDepartments: string[] = [];
  euDepartmentsFromAPI: any[] = [];
  spocOptions: string[] = ['SPOC 1', 'SPOC 2', 'SPOC 3'];
  purchaseRequisitions: string[] = [];
  purchaseRequisitionsFromAPI: any[] = [];
  
  // Vendor options
  vendorAccounts: string[] = [];
  vendorDisplayToId: { [key: string]: string } = {};
  vendorNames: { [key: string]: string } = {};
  vendorsFromAPI: any[] = [];
  evaluationTypes: string[] = ['Technical', 'Commercial'];
  sourceTypes: string[] = [];
  recommendedVendors: string[] = [];

  constructor(
    private fb: FormBuilder,
    private _router: Router,
    private _route: ActivatedRoute,
    private _loaderService: LoaderService,
    private apiService: ApiService,
    private toasterService: ToasterService,
    private tenderHelper: TenderHelperService,
    private cdr: ChangeDetectorRef
  ) {
    this.initializeForm();
  }

  ngOnInit(): void {
    // Check route to determine if we're in practice mode
    this.isFwaPracticeMode = this._router.url.includes('/fwa-practice');
    this.isPracticeMode = this._router.url.includes('/practice') && !this.isFwaPracticeMode;
    
    this.loadCurrencies();
    this.loadPurchaseRequisitions();
    this.loadVendors();
    this.loadDepartments();
    
    // Watch for tenderType changes to handle vendor fieldset visibility
    const tenderTypeControl = this.tenderForm.get('tenderType');
    if (tenderTypeControl) {
      this.tenderTypeSubscription = tenderTypeControl.valueChanges.subscribe((tenderType: string | null) => {
        if (tenderType && tenderType.toLowerCase() === 'public') {
          // Clear vendors when switching to Public
          while (this.selectedVendorsFormArray.length > 0) {
            this.selectedVendorsFormArray.removeAt(0);
          }
        }
      });
    }

    // Watch for Purchase Requisition changes
    this.setupPurchaseRequisitionWatcher();
  }

  private setupPurchaseRequisitionWatcher(): void {
    const purchaseRequisitionControl = this.tenderForm.get('purchaseRequisition');
    if (purchaseRequisitionControl) {
      purchaseRequisitionControl.valueChanges.subscribe(async (prNumber: string) => {
        if (prNumber && prNumber.trim() !== '') {
          await this.loadPRDetails(prNumber);
        } else {
          this.clearPRFields();
        }
      });
    }
  }

  private async loadPRDetails(prNumber: string): Promise<void> {
    try {
      // Fetch full PR details from API to get evaluation criteria
      this.apiService.setBaseUrl(environment.API_URL);
      const url = `${environment.API_URL}/api/${AppConstants.CONTROLLERS.PurchaseRequisition}/${AppConstants.ACTIONS.GetApprovedRecordByPRNumber}`;
      const response: any = await lastValueFrom(
        this.apiService.getByUrl(url, { prNumber })
      );

      if (!response?.isSuccess || !response?.data) {
        console.error('[Create Tender] Failed to fetch PR details:', response?.errorMessageEn);
        return;
      }

      const pr = response.data;
      this.selectedPR = pr;
      this.isPRSelected = true;
      
      // Auto-populate fields from PR and disable them
      const tenderValue = this.getPropertyValue(pr, 'value', 'Value', 'tenderValue', 'TenderValue');
      const prName = this.getPropertyValue(pr, 'prName', 'PRName', 'name', 'Name');
      const description = this.getPropertyValue(pr, 'description', 'Description');
      const recommendation = this.getPropertyValue(pr, 'justification', 'Justification', 'recommendationJustification', 'RecommendationJustification');
      
      // Set values and disable fields (they're auto-populated from PR)
      if (tenderValue) {
        this.tenderForm.patchValue({ tenderValue }, { emitEvent: false });
      }
      this.tenderForm.get('tenderValue')?.disable({ emitEvent: false });
      
      // Auto-fill tender name with PR name and disable
      if (prName) {
        this.tenderForm.patchValue({ tenderName: prName }, { emitEvent: false });
      }
      this.tenderForm.get('tenderName')?.disable({ emitEvent: false });
      
      if (description) {
        this.tenderForm.patchValue({ note: description }, { emitEvent: false });
      }
      this.tenderForm.get('note')?.disable({ emitEvent: false });
      
      if (recommendation) {
        this.tenderForm.patchValue({ recommendationJustification: recommendation }, { emitEvent: false });
      }
      this.tenderForm.get('recommendationJustification')?.disable({ emitEvent: false });

      // Auto-fill evaluation criteria from PR (but keep all fields editable)
      const evaluationType = this.getPropertyValue(pr, 'evaluationType', 'EvaluationType') || 'Separate';
      const technicalWeightage = this.getPropertyValue(pr, 'technicalWeightage', 'TechnicalWeightage');
      const commercialWeightage = this.getPropertyValue(pr, 'commercialWeightage', 'CommercialWeightage');
      const technicalCriteria = pr.technicalEvaluationCriteria || pr.TechnicalEvaluationCriteria || [];
      const commercialCriteria = pr.commercialEvaluationCriteria || pr.CommercialEvaluationCriteria || [];

      // Parse weightages
      const techWeight = technicalWeightage !== null && technicalWeightage !== undefined 
        ? (typeof technicalWeightage === 'string' ? parseFloat(technicalWeightage) : Number(technicalWeightage))
        : 100.00;
      const commWeight = commercialWeightage !== null && commercialWeightage !== undefined
        ? (typeof commercialWeightage === 'string' ? parseFloat(commercialWeightage) : Number(commercialWeightage))
        : 100.00;

      // Map criteria to the format expected by shared component
      const technicalCriteriaMapped = technicalCriteria.map((criteria: any) => ({
        description: criteria.description || criteria.Description || '',
        weightage: criteria.weightage || criteria.Weightage || 0
      }));

      const commercialCriteriaMapped = commercialCriteria.map((criteria: any) => ({
        description: criteria.description || criteria.Description || '',
        weightage: criteria.weightage || criteria.Weightage || 0
      }));

      // Update evaluation criteria data for shared component
      this.evaluationCriteriaData = {
        evaluationType: evaluationType,
        technicalWeightage: isNaN(techWeight) ? 100.00 : techWeight,
        commercialWeightage: isNaN(commWeight) ? 100.00 : commWeight,
        technicalEvaluationCriteria: technicalCriteriaMapped,
        commercialEvaluationCriteria: commercialCriteriaMapped
      };

      this.cdr.detectChanges();
    } catch (error) {
      console.error('[Create Tender] Error loading PR details:', error);
    }
  }

  private clearPRFields(): void {
    this.selectedPR = null;
    this.isPRSelected = false;
    
    // Re-enable fields when PR is cleared
    this.tenderForm.get('tenderName')?.enable({ emitEvent: false });
    this.tenderForm.get('tenderValue')?.enable({ emitEvent: false });
    this.tenderForm.get('note')?.enable({ emitEvent: false });
    this.tenderForm.get('recommendationJustification')?.enable({ emitEvent: false });
  }

  // Helper method to get field error message
  getFieldError(fieldName: string): string | null {
    const control = this.tenderForm.get(fieldName);
    if (!control || !control.touched || !control.errors) return null;
    
    if (control.errors['required']) {
      return 'This field is required';
    }
    return null;
  }

  // Helper method to check if field has any error
  hasFieldError(fieldName: string): boolean {
    const control = this.tenderForm.get(fieldName);
    return !!(control && control.invalid && control.touched);
  }

  private setupEvaluationTypeLogic(): void {
    const evaluationTypeControl = this.tenderForm.get('evaluationType');
    const technicalWeightageControl = this.tenderForm.get('technicalWeightage');
    const commercialWeightageControl = this.tenderForm.get('commercialWeightage');

    if (!evaluationTypeControl || !technicalWeightageControl || !commercialWeightageControl) {
      return;
    }

    // When evaluation type changes, adjust weightages accordingly
    this.evaluationTypeSubscription = evaluationTypeControl.valueChanges.subscribe((evaluationType: string) => {
      if (evaluationType === 'Joint') {
        // Joint evaluation: Set to 50-50 default, make editable
        const currentTotal = (parseFloat(technicalWeightageControl.value) || 0) + (parseFloat(commercialWeightageControl.value) || 0);
        if (currentTotal === 0 || currentTotal === 200) {
          // If coming from Separate (200) or empty, set to 50-50
          technicalWeightageControl.setValue(50.00, { emitEvent: false });
          commercialWeightageControl.setValue(50.00, { emitEvent: false });
        }
        // Enable controls for editing
        technicalWeightageControl.enable({ emitEvent: false });
        commercialWeightageControl.enable({ emitEvent: false });
      } else if (evaluationType === 'Separate') {
        // Separate evaluation: Both fixed at 100%, make readonly
        technicalWeightageControl.setValue(100.00, { emitEvent: false });
        commercialWeightageControl.setValue(100.00, { emitEvent: false });
        // Disable controls to make them readonly
        technicalWeightageControl.disable({ emitEvent: false });
        commercialWeightageControl.disable({ emitEvent: false });
      }
    });

    // When technical weightage changes in Joint mode, adjust commercial to maintain total of 100
    this.technicalWeightageSubscription = technicalWeightageControl.valueChanges.subscribe((value: any) => {
      const evaluationType = evaluationTypeControl.value;
      if (evaluationType === 'Joint') {
        const technicalWeightage = parseFloat(value) || 0;
        const commercialWeightage = Math.max(0, Math.min(100, 100 - technicalWeightage));
        commercialWeightageControl.setValue(commercialWeightage.toFixed(2), { emitEvent: false });
      }
    });

    // When commercial weightage changes in Joint mode, adjust technical to maintain total of 100
    this.commercialWeightageSubscription = commercialWeightageControl.valueChanges.subscribe((value: any) => {
      const evaluationType = evaluationTypeControl.value;
      if (evaluationType === 'Joint') {
        const commercialWeightage = parseFloat(value) || 0;
        const technicalWeightage = Math.max(0, Math.min(100, 100 - commercialWeightage));
        technicalWeightageControl.setValue(technicalWeightage.toFixed(2), { emitEvent: false });
      }
    });

    // Initialize based on default value (Separate)
    const initialEvaluationType = evaluationTypeControl.value;
    if (initialEvaluationType === 'Separate') {
      technicalWeightageControl.disable({ emitEvent: false });
      commercialWeightageControl.disable({ emitEvent: false });
    }

    // Subscribe to isJoiningFeeRequired changes to make joiningFee conditionally required
    const isJoiningFeeRequiredControl = this.tenderForm.get('isJoiningFeeRequired');
    const joiningFeeControl = this.tenderForm.get('joiningFee');
    
    if (isJoiningFeeRequiredControl && joiningFeeControl) {
      this.isJoiningFeeRequiredSubscription = isJoiningFeeRequiredControl.valueChanges.subscribe((value: string) => {
        if (value === 'Yes') {
          joiningFeeControl.setValidators([Validators.required]);
        } else {
          joiningFeeControl.clearValidators();
          joiningFeeControl.setValue(''); // Clear the value when not required
        }
        joiningFeeControl.updateValueAndValidity({ emitEvent: false });
      });
    }

    // Subscribe to tenderBondRequired changes to make tender bond fields conditionally required
    const tenderBondRequiredControl = this.tenderForm.get('tenderBondRequired');
    const tenderBondDetailsControl = this.tenderForm.get('tenderBondDetails');
    const tenderBondValidityDateControl = this.tenderForm.get('tenderBondValidityDate');
    
    if (tenderBondRequiredControl && tenderBondDetailsControl && tenderBondValidityDateControl) {
      this.tenderBondRequiredSubscription = tenderBondRequiredControl.valueChanges.subscribe((value: string) => {
        if (value === 'Yes') {
          tenderBondDetailsControl.setValidators([Validators.required]);
          tenderBondValidityDateControl.setValidators([Validators.required]);
        } else {
          tenderBondDetailsControl.clearValidators();
          tenderBondValidityDateControl.clearValidators();
          tenderBondDetailsControl.setValue(''); // Clear the value when not required
          tenderBondValidityDateControl.setValue(''); // Clear the value when not required
        }
        tenderBondDetailsControl.updateValueAndValidity({ emitEvent: false });
        tenderBondValidityDateControl.updateValueAndValidity({ emitEvent: false });
      });
    }

    // Subscribe to performanceBondRequired changes to make performance bond fields conditionally required
    const performanceBondRequiredControl = this.tenderForm.get('performanceBondRequired');
    const performanceBondDetailsControl = this.tenderForm.get('performanceBondDetails');
    const performanceBondValidityDateControl = this.tenderForm.get('performanceBondValidityDate');
    
    if (performanceBondRequiredControl && performanceBondDetailsControl && performanceBondValidityDateControl) {
      this.performanceBondRequiredSubscription = performanceBondRequiredControl.valueChanges.subscribe((value: string) => {
        if (value === 'Yes') {
          performanceBondDetailsControl.setValidators([Validators.required]);
          performanceBondValidityDateControl.setValidators([Validators.required]);
        } else {
          performanceBondDetailsControl.clearValidators();
          performanceBondValidityDateControl.clearValidators();
          performanceBondDetailsControl.setValue(''); // Clear the value when not required
          performanceBondValidityDateControl.setValue(''); // Clear the value when not required
        }
        performanceBondDetailsControl.updateValueAndValidity({ emitEvent: false });
        performanceBondValidityDateControl.updateValueAndValidity({ emitEvent: false });
      });
    }
  }
  
  ngOnDestroy(): void {
    if (this.tenderTypeSubscription) {
      this.tenderTypeSubscription.unsubscribe();
    }
    if (this.evaluationTypeSubscription) {
      this.evaluationTypeSubscription.unsubscribe();
    }
    if (this.technicalWeightageSubscription) {
      this.technicalWeightageSubscription.unsubscribe();
    }
    if (this.commercialWeightageSubscription) {
      this.commercialWeightageSubscription.unsubscribe();
    }
    if (this.isJoiningFeeRequiredSubscription) {
      this.isJoiningFeeRequiredSubscription.unsubscribe();
    }
    if (this.tenderBondRequiredSubscription) {
      this.tenderBondRequiredSubscription.unsubscribe();
    }
    if (this.performanceBondRequiredSubscription) {
      this.performanceBondRequiredSubscription.unsubscribe();
    }
  }
  
  get isPrivateTender(): boolean {
    const tenderType = this.tenderForm.get('tenderType')?.value;
    return tenderType && tenderType.toLowerCase() === 'private';
  }

  initializeForm(): void {
    this.tenderForm = this.fb.group({
      version: ['1'],
      tenderName: ['', Validators.required],
      status: ['Draft'],
      tenderType: ['', Validators.required],
      rfpRfq: ['', Validators.required],
      tenderClassification: [''],
      company: [''],
      euDepartment: [''],
      euDepartmentSpoc: [''],
      purchaseRequisition: ['', Validators.required],
      managedBySpoc: [''],
      managedByContactDetails: [''],
      currency: ['', Validators.required],
      tenderValue: ['', Validators.required],
      tenderValueInQAR: [''],
      awaitingValue: [''],
      awardingValueInQAR: [''],
      startDateOfTender: [''], // Required validator removed as field is hidden
      acceptanceDeadline: ['', Validators.required],
      extendedAcceptanceDeadline: [''],
      note: [''],
      recommendationJustification: [''],
      isJoiningFeeRequired: ['No'],
      joiningFee: [''],
      bidClosureDate: [''],
      isDraft: ['true'],
      backgroundAndSOW: [''],
      technicalEvaluation: [''],
      commercialEvaluation: [''],
      tenderBondRequired: ['No'],
      tenderBondDetails: [''],
      tenderBondValidityDate: [''],
      performanceBondRequired: ['No'],
      performanceBondDetails: [''],
      performanceBondValidityDate: [''],
      selectedVendors: this.fb.array([]),
      tenderDocuments: this.fb.array([]),
      bidDocuments: this.fb.array([])
    });

    // Initialize evaluation criteria data for shared component
    this.evaluationCriteriaData = {
      evaluationType: 'Separate',
      technicalWeightage: 100.00,
      commercialWeightage: 100.00,
      technicalEvaluationCriteria: [],
      commercialEvaluationCriteria: []
    };
  }

  onEvaluationCriteriaChange(data: EvaluationCriteriaData): void {
    // Store data for submission but don't update the input binding
    // to avoid triggering ngOnChanges which would repopulate and lose focus
    this._latestEvaluationData = data;
  }
  
  // Internal storage for latest evaluation data (used during form submission)
  private _latestEvaluationData?: EvaluationCriteriaData;

  // Helper methods - using TenderHelperService
  private getPropertyValue = (obj: any, ...keys: string[]) => this.tenderHelper.getPropertyValue(obj, ...keys);
  private getTrimmedValue = (value: any) => this.tenderHelper.getTrimmedValue(value);

  private async loadDataFromAPI(
    endpoint: string,
    propertyKeys: string[],
    apiDataProperty: string,
    displayDataProperty: string,
    fallbackData?: string[]
  ): Promise<void> {
    try {
      const url = `${environment.API_URL}${endpoint}`;
      const response: any = await lastValueFrom(this.apiService.getByUrl(url));
      const dataList = this.tenderHelper.extractArrayFromResponse(response);

      if (dataList.length > 0) {
        (this as any)[apiDataProperty] = dataList;
        (this as any)[displayDataProperty] = dataList
          .map((item: any) => this.getPropertyValue(item, ...propertyKeys))
          .filter((value: string) => value !== '');
      } else if (fallbackData) {
        (this as any)[displayDataProperty] = fallbackData;
      }
    } catch (error) {
      console.error(`[Create Tender] Error loading data from ${endpoint}:`, error);
      if (fallbackData) {
        (this as any)[displayDataProperty] = fallbackData;
      }
    }
  }

  async loadCurrencies(): Promise<void> {
    try {
      // Load all lookups from local storage (via ApiService)
      await this.apiService.loadAllLookups();
      
      // Currencies are loaded from local storage (allLookups_v2_anon) in ApiService
      this.currenciesFromAPI = this.apiService.currencies || [];
      
      // Extract currency values using the same property keys as before
      this.currencies = (this.apiService.currencies || [])
        .map((item: any) => this.getPropertyValue(item, 'currencyId', 'CurrencyId', 'currency', 'Currency'))
        .filter((value: string) => value !== '');
    } catch (error) {
      console.error('[Create Tender] Error loading currencies from local storage:', error);
    }
  }

  async loadPurchaseRequisitions(): Promise<void> {
    // Use different API endpoints based on mode
    const endpoint = `/api/PurchaseRequisition/${AppConstants.ACTIONS.GetAllApprovedPRsForTenderScope}?scope=${this.isFwaPracticeMode ? 1 : (this.isPracticeMode ? 0 : 2)}`;
    
    await this.loadDataFromAPI(
      endpoint,
      ['prNumber', 'PRNumber', 'pRNumber', 'PrNumber'],
      'purchaseRequisitionsFromAPI',
      'purchaseRequisitions'
    );
  }
  async loadVendors(): Promise<void> {
    try {
      const url = `${environment.API_URL}/api/Vendor/${AppConstants.ACTIONS.GetAllVendorsInfo}`;
      const response: any = await lastValueFrom(this.apiService.getByUrl(url));
      const vendorsList = this.tenderHelper.extractArrayFromResponse(response);

      if (vendorsList.length > 0) {
        this.vendorsFromAPI = vendorsList;
        // Extract vendor accounts and names - prioritize vendorID from new API structure
        this.vendorDisplayToId = {};
        this.vendorAccounts = vendorsList
          .map((vendor: any) => {
            const vendorId = this.getPropertyValue(vendor, 'vendorID', 'vendorId', 'VendorId', 'VendorID', 'id', 'Id');
            const vendorName = this.getVendorDisplayName(vendor, vendorId);
            if (vendorName && vendorId) {
              this.vendorDisplayToId[vendorName] = vendorId;
            }
            return vendorName;
          })
          .filter((value: string) => value !== '');

        // Build vendorNames mapping using fullName - handle empty strings and whitespace
        this.vendorNames = {};
        vendorsList.forEach((vendor: any) => {
          const vendorId = this.getPropertyValue(vendor, 'vendorID', 'vendorId', 'VendorId', 'VendorID', 'id', 'Id');
          const fullName = this.getPropertyValue(vendor, 'fullName', 'FullName', 'fullname', 'Fullname');
          // Only add to mapping if vendorId exists and fullName is not empty/whitespace
          if (vendorId && fullName && fullName.trim() !== '') {
            this.vendorNames[vendorId] = fullName.trim();
          }
        });
      }
    } catch (error) {
      console.error('[Create Tender] Error loading vendors:', error);
    }
  }

  async loadDepartments(): Promise<void> {
    try {
      const url = `${environment.API_URL}/api/Lookups/departments`;
      const response: any = await lastValueFrom(this.apiService.getByUrl(url));
      const rawData = Array.isArray(response) ? response : (response?.data || response?.Data || []);

      this.euDepartmentsFromAPI = Array.isArray(rawData) ? rawData : [];
      this.euDepartments = this.euDepartmentsFromAPI
        .map((department: any) => this.getTrimmedValue(this.getPropertyValue(department, 'departmentName', 'DepartmentName', 'name', 'Name')))
        .filter((departmentName: string) => departmentName !== '');
    } catch (error) {
      console.error('[Create Tender] Error loading departments:', error);
      this.euDepartmentsFromAPI = [];
      this.euDepartments = [];
    }
  }

  private getValidValue = (inputValue: any, apiData: any[], propertyKeys: string[]) =>
    this.tenderHelper.getValidValue(inputValue, apiData, propertyKeys, this.getPropertyValue);

  private getValidCurrencyId = (currencyValue: any): string | null =>
    this.getValidValue(currencyValue, this.currenciesFromAPI, ['currencyId', 'CurrencyId', 'currency', 'Currency']);

  private getValidPRNumber = (prValue: any): string | null =>
    this.getValidValue(prValue, this.purchaseRequisitionsFromAPI, ['prNumber', 'PRNumber', 'pRNumber', 'PrNumber']);

  private getValidDepartmentId(departmentValue: any): string | null {
    const selectedDepartment = this.getTrimmedValue(departmentValue);
    if (!selectedDepartment) return null;

    const matchedDepartment = this.euDepartmentsFromAPI.find((department: any) => {
      const departmentId = this.getTrimmedValue(this.getPropertyValue(department, 'departmentId', 'DepartmentId'));
      const departmentName = this.getTrimmedValue(this.getPropertyValue(department, 'departmentName', 'DepartmentName', 'name', 'Name'));
      return departmentId === selectedDepartment || departmentName === selectedDepartment;
    });

    return matchedDepartment
      ? this.getTrimmedValue(this.getPropertyValue(matchedDepartment, 'departmentId', 'DepartmentId'))
      : selectedDepartment;
  }

  get selectedVendorsFormArray(): FormArray {
    return this.tenderForm.get('selectedVendors') as FormArray;
  }

  // Get the current evaluation data (prefer latest from child, fallback to initial)
  private get currentEvaluationData(): EvaluationCriteriaData | undefined {
    return this._latestEvaluationData || this.evaluationCriteriaData;
  }

  // Wrapper getters that read from shared component data
  get technicalWeightage(): number {
    return this.currentEvaluationData?.technicalWeightage || 100.00;
  }

  get commercialWeightage(): number {
    return this.currentEvaluationData?.commercialWeightage || 100.00;
  }

  get totalWeightage(): number {
    return this.technicalWeightage + this.commercialWeightage;
  }

  get isJointEvaluation(): boolean {
    return this.currentEvaluationData?.evaluationType === 'Joint';
  }

  get isSeparateEvaluation(): boolean {
    return this.currentEvaluationData?.evaluationType === 'Separate';
  }

  getTotalTechnicalCriteriaWeightage(): number {
    if (!this.currentEvaluationData?.technicalEvaluationCriteria) return 0;
    return this.currentEvaluationData.technicalEvaluationCriteria.reduce((total, item) => {
      return total + (item.weightage || 0);
    }, 0);
  }

  getTotalCommercialCriteriaWeightage(): number {
    if (!this.currentEvaluationData?.commercialEvaluationCriteria) return 0;
    return this.currentEvaluationData.commercialEvaluationCriteria.reduce((total, item) => {
      return total + (item.weightage || 0);
    }, 0);
  }


  get tenderDocumentsFormArray(): FormArray {
    return this.tenderForm.get('tenderDocuments') as FormArray;
  }

  get bidDocumentsFormArray(): FormArray {
    return this.tenderForm.get('bidDocuments') as FormArray;
  }

  private createVendorGroup(): FormGroup {
    const vendorGroup = this.fb.group({
      vendorAccount: [''],
      vendorName: ['']
    });
    
    vendorGroup.get('vendorAccount')?.valueChanges.subscribe(account => {
      const selectedVendorId = account ? (this.vendorDisplayToId[account] || account) : '';
      const vendor = this.vendorsFromAPI.find((v: any) => {
        const vendorId = this.getPropertyValue(v, 'vendorId', 'VendorId', 'vendorID', 'VendorID', 'id', 'Id');
        return vendorId === selectedVendorId || this.getVendorDisplayName(v, vendorId) === account;
      });
      const vendorName = vendor ? this.getVendorNameOnly(vendor) : '';
      
      vendorGroup.patchValue({ vendorName }, { emitEvent: false });
    });
    
    return vendorGroup;
  }

  addSelectedVendor(): void {
    this.selectedVendorsFormArray.push(this.createVendorGroup());
  }

  private canRemoveItem(formArray: FormArray): boolean {
    return formArray.length > 1;
  }

  removeSelectedVendor(index: number): void {
    this.selectedVendorsFormArray.removeAt(index);
  }

  removeAllSelectedVendors(): void {
    const length = this.selectedVendorsFormArray.length;
    for (let i = length - 1; i > 0; i--) {
      this.selectedVendorsFormArray.removeAt(i);
    }
  }

  private updateSerialNumbers(formArray: FormArray): void {
    formArray.controls.forEach((control, i) => {
      control.patchValue({ serialNumber: i + 1 }, { emitEvent: false });
    });
  }

  goBack(): void {
    if (this.isFwaPracticeMode) {
      this._router.navigate([`/${AppConstants.ROUTES.FWA_PRACTICE}`]);
    } else if (this.isPracticeMode) {
      this._router.navigate([`/${AppConstants.ROUTES.PRACTICE}`]);
    } else {
      this._router.navigate([`/${AppConstants.ROUTES.TENDER}`]);
    }
  }

  private validateForm(): boolean {
    const formValue = this.tenderForm.value;
    const tenderType = this.getTrimmedValue(formValue.tenderType);
    const isPrivate = tenderType?.toLowerCase() === 'private';
    const evaluationType = this.currentEvaluationData?.evaluationType || 'Separate';
    
    // Validate joint evaluation total weightage
    if (evaluationType === 'Joint') {
      const total = this.totalWeightage;
      if (Math.abs(total - 100) > 0.01) { // Allow small floating point differences
        this.toasterService.showError(
          `For Joint evaluation, Technical and Commercial weightages must total exactly 100%. Current total: ${total.toFixed(2)}%`,
          'Validation Error'
        );
        return false;
      }
    }
    
    // Validate separate evaluation - both should be 100%
    if (evaluationType === 'Separate') {
      const technicalWeightage = this.technicalWeightage;
      const commercialWeightage = this.commercialWeightage;
      // Use a more lenient tolerance (0.1) to account for floating point precision issues
      const technicalValid = Math.abs(technicalWeightage - 100) <= 0.1;
      const commercialValid = Math.abs(commercialWeightage - 100) <= 0.1;
      
      if (!technicalValid || !commercialValid) {
        this.toasterService.showError(
          `For Separate evaluation, both Technical and Commercial weightages must be exactly 100%. Current: Technical=${technicalWeightage.toFixed(2)}%, Commercial=${commercialWeightage.toFixed(2)}%`,
          'Validation Error'
        );
        return false;
      }
    }

    // Separate evaluation scores Technical and Commercial independently. Each
    // criteria group must therefore be complete before the tender can be saved.
    if (evaluationType === 'Separate') {
      const technicalCriteriaTotal = this.getTotalTechnicalCriteriaWeightage();
      const commercialCriteriaTotal = this.getTotalCommercialCriteriaWeightage();

      if (Math.abs(technicalCriteriaTotal - 100) > 0.01) {
        this.toasterService.showError(
          `The sum of all Technical evaluation criteria items must equal exactly 100%. Current total: ${technicalCriteriaTotal.toFixed(2)}%`,
          'Validation Error'
        );
        return false;
      }

      if (Math.abs(commercialCriteriaTotal - 100) > 0.01) {
        this.toasterService.showError(
          `The sum of all Commercial evaluation criteria items must equal exactly 100%. Current total: ${commercialCriteriaTotal.toFixed(2)}%`,
          'Validation Error'
        );
        return false;
      }
    }

    // Validate evaluation criteria descriptions are not empty
    const technicalCriteria = this.currentEvaluationData?.technicalEvaluationCriteria || [];
    const commercialCriteria = this.currentEvaluationData?.commercialEvaluationCriteria || [];
    
    for (let i = 0; i < technicalCriteria.length; i++) {
      const description = this.getTrimmedValue(technicalCriteria[i]?.description);
      if (!description) {
        this.toasterService.showError(
          `Technical evaluation criteria row ${i + 1} must have a description`,
          'Validation Error'
        );
        return false;
      }
    }
    
    for (let i = 0; i < commercialCriteria.length; i++) {
      const description = this.getTrimmedValue(commercialCriteria[i]?.description);
      if (!description) {
        this.toasterService.showError(
          `Commercial evaluation criteria row ${i + 1} must have a description`,
          'Validation Error'
        );
        return false;
      }
    }
    
    const validations = [
      { check: !this.getTrimmedValue(formValue.version), message: 'Please enter a version' },
      { check: isPrivate && this.selectedVendorsFormArray.length === 0, message: 'Please add at least one vendor' }
    ];

    const failed = validations.find(v => v.check);
    if (failed) {
      this.toasterService.showError(failed.message, 'Validation Error');
      return false;
    }
    return true;
  }

  private formatDateForAPI = (dateValue: any) => this.tenderHelper.formatDateForAPI(dateValue);
  private parseNumber = (value: any) => this.tenderHelper.parseNumber(value);
  private normalizeBooleanString = (value: any) => this.tenderHelper.normalizeBooleanString(value);
  private convertYesNoToTrueFalse = (value: any) => this.tenderHelper.convertYesNoToTrueFalse(value);

  private mapFormArrayToPayload(
    formArray: FormArray,
    mapper: (control: any) => any
  ): any[] {
    return formArray.controls.map(mapper);
  }

  private prepareTenderPayload(): any {
    // Use getRawValue() to include disabled control values (from PR auto-population)
    const formValue = this.tenderForm.getRawValue();
    const version = this.getTrimmedValue(formValue.version) || '1';

    if (!version) {
      throw new Error('Version is required');
    }

    const payload: any = {
      version,
      status: this.getTrimmedValue(formValue.status) || 'Draft',
      isDraft: this.normalizeBooleanString(formValue.isDraft),
      isPractice: this.isPracticeMode, // Add practice mode flag
      isFwaPractice: this.isFwaPracticeMode
    };

    // Add string fields only if they have values
    const tenderName = this.getTrimmedValue(formValue.tenderName);
    if (tenderName) payload.tenderName = tenderName;

    const tenderType = this.getTrimmedValue(formValue.tenderType);
    if (tenderType) payload.tenderType = tenderType;

    const rfpRfq = this.getTrimmedValue(formValue.rfpRfq);
    if (rfpRfq) payload.rfpRfq = rfpRfq;

    const tenderClassification = this.getTrimmedValue(formValue.tenderClassification);
    if (tenderClassification) payload.tenderClassification = tenderClassification;

    const company = this.getTrimmedValue(formValue.company);
    if (company) payload.company = company;

    const euDepartment = this.getValidDepartmentId(formValue.euDepartment);
    if (euDepartment) payload.euDepartment = euDepartment;

    const euDepartmentSpoc = this.getTrimmedValue(formValue.euDepartmentSpoc);
    if (euDepartmentSpoc) payload.euDepartmentSpoc = euDepartmentSpoc;

    const purchaseRequisition = this.getValidPRNumber(formValue.purchaseRequisition) || this.getTrimmedValue(formValue.purchaseRequisition);
    if (purchaseRequisition) payload.purchaseRequisition = purchaseRequisition;

    const managedBySpoc = this.getTrimmedValue(formValue.managedBySpoc);
    if (managedBySpoc) payload.managedBySpoc = managedBySpoc;

    const managedByContactDetails = this.getTrimmedValue(formValue.managedByContactDetails);
    if (managedByContactDetails) payload.managedByContactDetails = managedByContactDetails;

    const currency = this.getValidCurrencyId(formValue.currency) || this.getTrimmedValue(formValue.currency);
    if (currency) payload.currency = currency;

    // Add number fields only if they have values (greater than 0)
    const tenderValue = this.parseNumber(formValue.tenderValue);
    if (tenderValue > 0) payload.tenderValue = tenderValue;

    const tenderValueInQAR = this.parseNumber(formValue.tenderValueInQAR);
    if (tenderValueInQAR > 0) payload.tenderValueInQAR = tenderValueInQAR;

    const awaitingValue = this.parseNumber(formValue.awaitingValue);
    if (awaitingValue > 0) payload.awaitingValue = awaitingValue;

    const awardingValueInQAR = this.parseNumber(formValue.awardingValueInQAR);
    if (awardingValueInQAR > 0) payload.awardingValueInQAR = awardingValueInQAR;

    const joiningFee = this.parseNumber(formValue.joiningFee);
    if (joiningFee > 0) payload.joiningFee = joiningFee;

    // Add date fields only if they have values
    const startDateOfTender = this.formatDateForAPI(formValue.startDateOfTender);
    if (startDateOfTender) payload.startDateOfTender = startDateOfTender;

    const acceptanceDeadline = this.formatDateForAPI(formValue.acceptanceDeadline);
    if (acceptanceDeadline) payload.acceptanceDeadline = acceptanceDeadline;

    const extendedAcceptanceDeadline = this.formatDateForAPI(formValue.extendedAcceptanceDeadline);
    if (extendedAcceptanceDeadline) payload.extendedAcceptanceDeadline = extendedAcceptanceDeadline;

    const bidClosureDate = this.formatDateForAPI(formValue.bidClosureDate);
    if (bidClosureDate) payload.bidClosureDate = bidClosureDate;

    const tenderBondValidityDate = this.formatDateForAPI(formValue.tenderBondValidityDate);
    if (tenderBondValidityDate) payload.tenderBondValidityDate = tenderBondValidityDate;

    const performanceBondValidityDate = this.formatDateForAPI(formValue.performanceBondValidityDate);
    if (performanceBondValidityDate) payload.performanceBondValidityDate = performanceBondValidityDate;

    // Add text fields only if they have values
    const note = this.getTrimmedValue(formValue.note);
    if (note) payload.note = note;

    const recommendationJustification = this.getTrimmedValue(formValue.recommendationJustification);
    if (recommendationJustification) payload.recommendationJustification = recommendationJustification;

    const backgroundAndSOW = this.getTrimmedValue(formValue.backgroundAndSOW);
    if (backgroundAndSOW) payload.backgroundAndSOW = backgroundAndSOW;

    const technicalEvaluation = this.getTrimmedValue(formValue.technicalEvaluation);
    if (technicalEvaluation) payload.technicalEvaluation = technicalEvaluation;

    const commercialEvaluation = this.getTrimmedValue(formValue.commercialEvaluation);
    if (commercialEvaluation) payload.commercialEvaluation = commercialEvaluation;

    const tenderBondDetails = this.getTrimmedValue(formValue.tenderBondDetails);
    if (tenderBondDetails) payload.tenderBondDetails = tenderBondDetails;

    const performanceBondDetails = this.getTrimmedValue(formValue.performanceBondDetails);
    if (performanceBondDetails) payload.performanceBondDetails = performanceBondDetails;

    // Add boolean fields (always include them)
    const isJoiningFeeRequired = this.convertYesNoToTrueFalse(formValue.isJoiningFeeRequired);
    if (isJoiningFeeRequired !== null && isJoiningFeeRequired !== undefined) {
      payload.isJoiningFeeRequired = isJoiningFeeRequired;
    }

    const tenderBondRequired = this.convertYesNoToTrueFalse(formValue.tenderBondRequired);
    if (tenderBondRequired !== null && tenderBondRequired !== undefined) {
      payload.tenderBondRequired = tenderBondRequired;
    }

    const performanceBondRequired = this.convertYesNoToTrueFalse(formValue.performanceBondRequired);
    if (performanceBondRequired !== null && performanceBondRequired !== undefined) {
      payload.performanceBondRequired = performanceBondRequired;
    }

    // Add evaluation criteria fields from shared component
    if (this.currentEvaluationData) {
      payload.evaluationType = this.currentEvaluationData.evaluationType || 'Separate';
      payload.technicalWeightage = this.currentEvaluationData.technicalWeightage || 100.00;
      payload.commercialWeightage = this.currentEvaluationData.commercialWeightage || 100.00;
    } else {
      payload.evaluationType = 'Separate';
      payload.technicalWeightage = 100.00;
      payload.commercialWeightage = 100.00;
    }

    // Add arrays - only include vendors for Private tenders
    const isPrivate = tenderType && tenderType.toLowerCase() === 'private';
    
    if (isPrivate) {
      const selectedVendors = this.mapFormArrayToPayload(
        this.selectedVendorsFormArray,
        (control: any) => ({
          vendorId: this.getTrimmedValue(this.vendorDisplayToId[control.get('vendorAccount')?.value] || control.get('vendorAccount')?.value),
          vendorName: this.getTrimmedValue(control.get('vendorName')?.value)
        })
      ).filter((v: any) => v.vendorId !== '');
      if (selectedVendors.length > 0) {
        payload.selectedVendors = selectedVendors;
      }
    }
    // Add technical evaluation criteria from shared component
    if (this.currentEvaluationData?.technicalEvaluationCriteria && this.currentEvaluationData.technicalEvaluationCriteria.length > 0) {
      payload.technicalEvaluationCriteria = this.currentEvaluationData.technicalEvaluationCriteria
        .filter((item: any) => item.description && item.description.trim() !== '');
    }

    // Add commercial evaluation criteria from shared component
    if (this.currentEvaluationData?.commercialEvaluationCriteria && this.currentEvaluationData.commercialEvaluationCriteria.length > 0) {
      payload.commercialEvaluationCriteria = this.currentEvaluationData.commercialEvaluationCriteria
        .filter((item: any) => item.description && item.description.trim() !== '');
    }

    // Add tender documents
    const tenderDocuments = this.mapFormArrayToPayload(
      this.tenderDocumentsFormArray,
      (control: any) => {
        const doc: any = {
          name: this.getTrimmedValue(control.get('name')?.value),
          fileContent: control.get('fileContent')?.value,
          mimeType: control.get('mimeType')?.value,
          attachmentFor: 'Tender',
          additionalDetails: this.getTrimmedValue(control.get('additionalDetails')?.value) || ''
        };
        return doc;
      }
    ).filter((d: any) => d.name && d.fileContent);
    if (tenderDocuments.length > 0) {
      payload.tenderDocuments = tenderDocuments;
    }

    return payload;
  }
  private createDocumentGroup(): FormGroup {
    return this.fb.group({
      name: [''],
      fileContent: [''],
      mimeType: [''],
      additionalDetails: ['']
    });
  }

  addTenderDocument(): void {
    this.tenderDocumentsFormArray.push(this.createDocumentGroup());
  }

  removeTenderDocument(index: number): void {
    this.tenderDocumentsFormArray.removeAt(index);
  }

  onFileSelected(event: any, index: number, documentType: 'tender' | 'bid'): void {
    const file = event.target.files[0];
    if (!file) return;

    const formArray = documentType === 'tender' ? this.tenderDocumentsFormArray : this.bidDocumentsFormArray;
    const documentControl = formArray.at(index);

    if (documentControl) {
      // Update name field immediately
      const currentName = documentControl.get('name')?.value;
      if (!currentName || currentName.trim() === '') {
        documentControl.patchValue({ name: file.name });
      }

      const reader = new FileReader();
      reader.onload = (e: any) => {
        const base64String = e.target.result;
        // Extract base64 data (remove data:type;base64, prefix if present)
        const base64Data = base64String.includes(',') ? base64String.split(',')[1] : base64String;
        
        documentControl.patchValue({
          fileContent: base64Data,
          mimeType: file.type || this.getMimeTypeFromFileName(file.name)
        });
      };
      reader.onerror = (error) => {
        console.error('Error reading file:', error);
        this.toasterService.showError('Error reading file. Please try again.', 'File Error');
      };
      reader.readAsDataURL(file);
    }
  }

  private getMimeTypeFromFileName(fileName: string): string {
    const extension = fileName.substring(fileName.lastIndexOf('.') + 1).toLowerCase();
    const mimeTypes: { [key: string]: string } = {
      'pdf': 'application/pdf',
      'jpg': 'image/jpeg',
      'jpeg': 'image/jpeg',
      'png': 'image/png',
      'doc': 'application/msword',
      'docx': 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
      'xls': 'application/vnd.ms-excel',
      'xlsx': 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
    };
    return mimeTypes[extension] || 'application/octet-stream';
  }


  private getVendorDisplayName(vendor: any, vendorId?: string): string {
    const normalizedVendorId = this.getTrimmedValue(vendorId || this.getPropertyValue(vendor, 'vendorID', 'vendorId', 'VendorId', 'VendorID', 'id', 'Id'));
    const vendorName = this.getVendorNameOnly(vendor);

    if (normalizedVendorId && vendorName) {
      return `${normalizedVendorId} - ${vendorName}`;
    }

    return vendorName || normalizedVendorId || '';
  }

  private getVendorNameOnly(vendor: any): string {
    return this.getTrimmedValue(this.getPropertyValue(vendor, 'vendorName', 'VendorName', 'fullName', 'FullName', 'name', 'Name'));
  }
  private getErrorMessage = (error: any) => this.tenderHelper.getErrorMessage(error);
  private getResponseErrorMessage = (response: any, defaultMessage: string) => 
    this.tenderHelper.getResponseErrorMessage(response, defaultMessage);
  private isSuccessResponse = (response: any) => this.tenderHelper.isSuccessResponse(response);

  private async publishTender(tenderNumber: string, version: string): Promise<boolean> {
    try {
      const publishPayload = {
        version: version,
        applicationID: tenderNumber,
        processName: 'TENDER'
      };

      const url = `${environment.API_URL}/api/${AppConstants.CONTROLLERS.Common}/${AppConstants.ACTIONS.AdminPublishRequest}`;
      const options: any = {
        headers: new HttpHeaders({
          'Language': 'en' // Default language, can be made dynamic if needed
        })
      };

      const response: any = await lastValueFrom(
        this.apiService.postByUrl(url, publishPayload, {}, options)
      );

      return this.isSuccessResponse(response);
    } catch (error: any) {
      console.error('[Create Tender] Error publishing tender:', error);
      return false;
    }
  }

  /**
   * Recursively marks all fields in a FormGroup or FormArray as touched
   */
  private markFormGroupTouched(formGroup: FormGroup | FormArray): void {
    Object.keys(formGroup.controls).forEach(key => {
      const control = formGroup.get(key);
      
      if (control instanceof FormGroup || control instanceof FormArray) {
        this.markFormGroupTouched(control);
      } else {
        control?.markAsTouched();
      }
    });
  }

  async createTender(): Promise<void> {
    // Mark all form fields as touched to show validation errors
    this.markFormGroupTouched(this.tenderForm);
    
    // Check if form is valid
    if (this.tenderForm.invalid) {
      this.toasterService.showError('Please fill in all required fields correctly', 'Validation Error');
      return;
    }
    
    if (!this.validateForm()) return;

    try {
      const payload = this.prepareTenderPayload();
      this._loaderService.showLoader();

      const url = `${environment.API_URL}/api/Tenders/${AppConstants.ACTIONS.AdminCreateOrUpdateTenderNew}`;
      const response: any = await lastValueFrom(this.apiService.postByUrl(url, payload));

      if (this.isSuccessResponse(response)) {
        // Extract tenderNumber from response (backend auto-generates it)
        const tenderNumber = response.data?.tenderNumber || 
                            response.tenderNumber || 
                            response.data?.applicationID || 
                            '';
        
        // Publish the tender after successful creation
        const publishSuccess = tenderNumber ? await this.publishTender(tenderNumber, payload.version) : false;
        
        this._loaderService.hideLoader();

        const entityName = this.isFwaPracticeMode ? 'FWA-Practice' : (this.isPracticeMode ? 'Practice' : 'Tender');
        if (publishSuccess) {
          this.toasterService.showSuccess(`${entityName} created and published successfully`, 'Success');
        } else {
          this.toasterService.showSuccess(`${entityName} created successfully`, 'Success');
        }
        
        const redirectRoute = this.isFwaPracticeMode ? AppConstants.ROUTES.FWA_PRACTICE : (this.isPracticeMode ? AppConstants.ROUTES.PRACTICE : AppConstants.ROUTES.TENDER);
        setTimeout(() => this._router.navigate([`/${redirectRoute}`]), 1500);
      } else {
        this._loaderService.hideLoader();
        this.toasterService.showError(
          this.getResponseErrorMessage(response, 'Failed to create tender'),
          'Error'
        );
      }
    } catch (error: any) {
      this._loaderService.hideLoader();
      this.toasterService.showError(
        this.getErrorMessage(error) || 'An error occurred while creating the tender',
        'Error'
      );
      console.error('[Create Tender] Error:', error);
    }
  }
}
